export type RandomFn = () => number;

export const SPEED_TEST_SCENARIO_TIMING = {
  preparingMs: 1000,
  pingMs: 3000,
  downloadMs: 8000,
  uploadMs: 8000,
  tickMs: 100,
} as const;

export const PING_MS_RANGE = { min: 10, max: 20 } as const;
export const JITTER_MS_RANGE = { min: 1, max: 4 } as const;

export interface ContractedMbpsPlan {
  downloadMbps: number;
  uploadMbps: number;
}

export function isPositiveFiniteMbps(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

export function resolveContractedPlan(
  services:
    | ReadonlyArray<{
        contractedPlan?: { downloadMbps?: number | null; uploadMbps?: number | null } | null;
      }>
    | null
    | undefined,
): ContractedMbpsPlan | null {
  const plan = services?.[0]?.contractedPlan;
  if (!plan) {
    return null;
  }

  if (!isPositiveFiniteMbps(plan.downloadMbps) || !isPositiveFiniteMbps(plan.uploadMbps)) {
    return null;
  }

  return {
    downloadMbps: plan.downloadMbps,
    uploadMbps: plan.uploadMbps,
  };
}

export function pickInRange(min: number, max: number, random: RandomFn): number {
  return min + (max - min) * random();
}

export function downloadFinalRange(planMbps: number): { min: number; max: number } {
  return { min: Math.max(0, planMbps - 0.8), max: planMbps + 5 };
}

export function uploadFinalRange(planMbps: number): { min: number; max: number } {
  return { min: Math.max(0, planMbps - 0.5), max: planMbps + 3 };
}

export function downloadAnimBand(planMbps: number): { min: number; max: number } {
  return { min: planMbps * 0.95, max: planMbps * 1.05 };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function roundMetric(value: number): number {
  return Math.round(value * 100) / 100;
}

export function easeOutCubic(t: number): number {
  const x = clamp01(t);
  return 1 - (1 - x) ** 3;
}

/** Starts at `from`, eases toward `to`, with a damped sine. Result is never negative. */
export function approachWithOscillation(t: number, from: number, to: number, amplitude: number): number {
  const x = clamp01(t);
  const eased = from + (to - from) * easeOutCubic(x);
  const oscillation = amplitude * Math.sin(x * Math.PI * 8) * (1 - x);
  return Math.max(0, eased + oscillation);
}

export function sampleDownloadDuring(t: number, planMbps: number): number {
  const band = downloadAnimBand(planMbps);
  const raw = approachWithOscillation(t, band.min, planMbps, planMbps * 0.03);
  return clamp(raw, band.min, band.max);
}

export function sampleUploadDuring(t: number, planMbps: number): number {
  return approachWithOscillation(t, planMbps * 0.55, planMbps, planMbps * 0.08);
}

export function samplePingDuring(t: number, pingFinalMs: number): number {
  return approachWithOscillation(t, pingFinalMs * 0.4, pingFinalMs, 0.4);
}

export function sampleJitterDuring(t: number, jitterFinalMs: number): number {
  return approachWithOscillation(t, jitterFinalMs * 0.4, jitterFinalMs, 0.15);
}
